import { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/joy";
import { useLocation, useNavigate } from "react-router-dom";

import {
    useAuthentication,
    useUserRoles,
    useUser,
    useOrganization,
} from "../../hooks/query";
import { useAuth } from "../../hooks";
import { axiosPrivate } from "../../service";
import { NoOrganizationAccess } from "../";
import {
    loadAuthSession,
    saveAuthSession,
} from "../../utils/offlineAuthSession";

const SELECTED_ORG_STORAGE_KEY = "selectedOrganizationId";

function resolveOrganizationId(organizations: any[]): string | null {
    if (!organizations.length) return null;
    const savedId = sessionStorage.getItem(SELECTED_ORG_STORAGE_KEY);
    const selected =
        organizations.find((org) => org.Id === savedId) || organizations[0];
    return selected?.Id || null;
}

export default function AuthHandler({
    children,
}: {
    children: React.ReactNode;
}) {
    const {
        setIsAuthenticated,
        setUser,
        setToken,
        setRoles,
        setOrganizations,
        isAuthenticated,
        token,
    } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [noOrganization, setNoOrganization] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const { useTokenExchange } = useAuthentication();
    const tokenExchangeSvc = useTokenExchange();

    const { useListByUser } = useUserRoles();
    const listRoleByUserSvc = useListByUser();

    const { useGetUserById } = useUser();
    const getUserByIdSvc = useGetUserById();

    const { useListByUser: useListOrganizationsByUser } = useOrganization();
    const listOrganizationsByUserSvc = useListOrganizationsByUser();

    const hydrateFromSession = async (): Promise<boolean> => {
        const session = await loadAuthSession();
        if (!session?.user && !session?.organizations?.length) {
            return false;
        }

        if (session.token) {
            axiosPrivate.defaults.headers.common.Authorization = `Bearer ${session.token}`;
            setToken(session.token);
        }
        if (session.selectedOrganizationId) {
            sessionStorage.setItem(
                SELECTED_ORG_STORAGE_KEY,
                session.selectedOrganizationId,
            );
        }
        setUser(session.user);
        setRoles(session.roles || []);
        setOrganizations(session.organizations || []);
        setIsAuthenticated(true);
        return true;
    };

    const persistSession = async (params: {
        accessToken: string;
        user: any;
        roles: any[];
        organizations: any[];
    }) => {
        const organizationId = resolveOrganizationId(params.organizations);
        await saveAuthSession({
            token: params.accessToken,
            user: params.user,
            roles: params.roles,
            organizations: params.organizations,
            selectedOrganizationId: organizationId,
        });
    };

    const fetchToken = async () => {
        const isAuthRoute = location.pathname.startsWith("/auth/");
        if (isAuthRoute) {
            setIsLoading(false);
            return;
        }

        if (isAuthenticated || token) {
            setIsLoading(false);
            return;
        }

        const offline = typeof navigator !== "undefined" && !navigator.onLine;
        if (offline) {
            const ok = await hydrateFromSession();
            setIsLoading(false);
            if (!ok) {
                // No cached session — ProtectedRoute will send to landing
            }
            return;
        }

        try {
            setIsLoading(true);
            const tokenResponse = await tokenExchangeSvc.mutateAsync();
            axiosPrivate.defaults.headers.common.Authorization = `Bearer ${tokenResponse}`;
            setToken(tokenResponse);

            const organizationsResponse =
                await listOrganizationsByUserSvc.mutateAsync();
            const organizations = Array.isArray(organizationsResponse)
                ? organizationsResponse
                : organizationsResponse
                  ? [organizationsResponse]
                  : [];

            if (!organizations.length) {
                setNoOrganization(true);
                setIsLoading(false);
                return;
            }

            const organizationId = resolveOrganizationId(organizations);
            if (organizationId) {
                sessionStorage.setItem(
                    SELECTED_ORG_STORAGE_KEY,
                    organizationId,
                );
            }

            const listRoleByUserResponse = await listRoleByUserSvc.mutateAsync(
                {},
            );
            const roles = listRoleByUserResponse || [];
            const userId = roles[0]?.UserId as string | undefined;

            let userResponse = null;
            if (userId && organizationId) {
                try {
                    userResponse = await getUserByIdSvc.mutateAsync({
                        id: userId,
                        OrganizationId: organizationId,
                    });
                } catch (profileError) {
                    console.error(profileError);
                }
            }

            const user = userResponse || (userId ? { Id: userId } : null);
            setUser(user);
            setRoles(roles);
            setOrganizations(organizations);
            setIsAuthenticated(true);
            await persistSession({
                accessToken: tokenResponse,
                user,
                roles,
                organizations,
            });
            setIsLoading(false);
        } catch (e) {
            console.error(e);
            const ok = await hydrateFromSession();
            setIsLoading(false);
            if (!ok) {
                console.warn("Auth bootstrap failed and no offline session");
            }
        }
    };

    useEffect(() => {
        fetchToken().then();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (noOrganization) {
        return (
            <NoOrganizationAccess
                onBackToLogin={() => navigate("/auth/landing")}
            />
        );
    }

    if (isLoading) {
        return (
            <Box
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
                justifyContent={"center"}
                minHeight={"100vh"}
                gap={0}
                textAlign={"center"}
            >
                <CircularProgress size="sm" variant="plain" sx={{ mb: 1 }} />
                <Typography level="title-lg">Signing you in…</Typography>
                <Typography level="body-sm" color="neutral">
                    Preparing your workspace
                </Typography>
            </Box>
        );
    }
    return <>{children}</>;
}
